import { useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  ShieldAlert, Droplets, ArrowDown, FlaskConical,
  Layers, Beaker, Wind, Waves, Database, Cylinder,
  Microscope, TestTube
} from 'lucide-react';
import AdminDAFValidation from './AdminDAFValidation';
import AdminLamellaValidation from './AdminLamellaValidation';
import AdminGACValidation from './AdminGACValidation';
import AdminMultimediaValidation from './AdminMultimediaValidation';
import AdminEQValidation from './AdminEQValidation';
import AdminPHValidation from './AdminPHValidation';
import AdminAerationValidation from './AdminAerationValidation';
import AdminSecondaryValidation from './AdminSecondaryValidation';
import AdminBufferValidation from './AdminBufferValidation';
import AdminSludgeValidation from './AdminSludgeValidation';
import PageHeader from '@/components/PageHeader';

type EquipmentTab =
  | 'daf' | 'lamella' | 'gac' | 'multimedia' | 'eq'
  | 'ph' | 'aeration' | 'secondary' | 'buffer' | 'sludge';

interface TabConfig {
  id: EquipmentTab;
  label: string;
  icon: React.ElementType;
  component: React.ComponentType;
}

const TABS: TabConfig[] = [
  { id: 'daf', label: 'DAF', icon: Droplets, component: AdminDAFValidation },
  { id: 'lamella', label: 'Lamella', icon: ArrowDown, component: AdminLamellaValidation },
  { id: 'gac', label: 'GAC', icon: Database, component: AdminGACValidation },
  { id: 'multimedia', label: 'Multimedia', icon: Layers, component: AdminMultimediaValidation },
  { id: 'eq', label: 'EQ Tank', icon: Waves, component: AdminEQValidation },
  { id: 'ph', label: 'pH', icon: Beaker, component: AdminPHValidation },
  { id: 'aeration', label: 'Aeration', icon: Wind, component: AdminAerationValidation },
  { id: 'secondary', label: 'Secondary', icon: FlaskConical, component: AdminSecondaryValidation },
  { id: 'buffer', label: 'Buffer', icon: Waves, component: AdminBufferValidation },
  { id: 'sludge', label: 'Sludge', icon: Cylinder, component: AdminSludgeValidation },
];

export default function EquipmentValidationHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as EquipmentTab | null;
  const [activeTab, setActiveTab] = useState<EquipmentTab>(
    TABS.find((t) => t.id === tabFromUrl) ? (tabFromUrl as EquipmentTab) : 'daf'
  );

  const handleTabChange = (id: EquipmentTab) => {
    setActiveTab(id);
    setSearchParams({ tab: id });
  };

  const ActiveComponent = TABS.find((t) => t.id === activeTab)?.component ?? AdminDAFValidation;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Microscope}
        iconColor="text-sky-400"
        title="Equipment Validation Hub"
        subtitle="Scientific equation audit across all process equipment — validate against live database samples"
        badge={
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-sky-500/5 border border-sky-500/20">
            <TestTube className="w-3.5 h-3.5 text-sky-400" />
            <span className="text-xs text-sky-400 font-bold">Admin Audit Tool</span>
          </div>
        }
      />

      {/* Tab Navigation */}
      <div className="p-2 rb-card">
        <div className="flex flex-wrap gap-1">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-sky-500/15 text-sky-400 border border-sky-500/30'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 border border-transparent'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active Validation Component */}
      <div className="min-h-[600px]">
        <ActiveComponent />
      </div>
    </div>
  );
}
